const STORAGE_PREFIX = "country-redirect-popup:";
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class CountryRedirectPopup extends HTMLElement {
  connectedCallback() {
    this.panels = Array.from(
      this.querySelectorAll("[data-country-redirect-panel]"),
    );
    if (this.panels.length === 0) return;

    this.activePanel = null;
    this.lastFocusedElement = null;
    this.onKeydown = this.handleKeydown.bind(this);

    this.init();
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.onKeydown);
    document.documentElement.classList.remove("country-redirect-popup--locked");
    window.clearInterval(this.countdownTimer);
  }

  get delay() {
    return parseFloat(this.dataset.delay || "0") * 1000;
  }

  get rememberDays() {
    return parseInt(this.dataset.rememberDays || "7", 10);
  }

  get isTestMode() {
    return this.dataset.testMode === "true";
  }

  get isDesignMode() {
    return this.dataset.designMode === "true";
  }

  get forcedCountry() {
    return (this.dataset.testCountry || "").trim().toUpperCase();
  }

  get locksScroll() {
    return this.dataset.lockScroll === "true";
  }

  get forceCountdown() {
    return parseInt(this.dataset.forceCountdown || "0", 10);
  }

  get forceMessage() {
    return this.dataset.forceMessage || "";
  }

  // Escape hatch: ?country_redirect=off loads the page without being sent away,
  // so a forced entry can never lock staff out of their own storefront.
  get redirectSuppressed() {
    try {
      return (
        new URLSearchParams(window.location.search).get("country_redirect") ===
        "off"
      );
    } catch (error) {
      return false;
    }
  }

  isForced(panel) {
    return panel.dataset.force === "true";
  }

  async init() {
    const country = await this.resolveCountry();
    if (!country) return;

    const panel = this.panels.find((panel) =>
      this.countriesFor(panel).includes(country),
    );
    if (!panel) return;

    // A forced entry ignores dismissal entirely — there is no way to dismiss it.
    // Test mode is handled inside isDismissed().
    if (!this.isForced(panel) && this.isDismissed(panel)) return;

    // Skip the delay only while styling in the editor, so the merchant is not
    // waiting on a timer every reload.
    const delay = this.isDesignMode && this.isTestMode ? 0 : this.delay;
    window.setTimeout(() => this.open(panel), delay);
  }

  // Test mode is the only thing that bypasses real geo-detection. With it off, the
  // theme editor behaves exactly like a live visit — including not showing at all.
  async resolveCountry() {
    if (this.isTestMode) {
      if (this.forcedCountry) return this.forcedCountry;

      // Geo-detection is unreliable inside the editor's iframe, so preview the
      // first entry instead. On the storefront, fall through to real detection.
      if (this.isDesignMode) {
        return this.countriesFor(this.panels[0])[0] || null;
      }
    }

    return this.detectCountry();
  }

  // Shopify resolves the visitor's country from their IP on this endpoint — the same one
  // the official Geolocation app uses. It is the only geo source available to a theme.
  async detectCountry() {
    try {
      const response = await fetch(
        "/browsing_context_suggestions.json?country[enabled]=true",
        {
          headers: { Accept: "application/json" },
        },
      );
      if (!response.ok) return null;

      const data = await response.json();
      const handle =
        data &&
        data.detected_values &&
        data.detected_values.country &&
        data.detected_values.country.handle;
      return handle ? handle.toUpperCase() : null;
    } catch (error) {
      return null;
    }
  }

  countriesFor(panel) {
    return (panel.dataset.country || "")
      .split(",")
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean);
  }

  storageKey(panel) {
    return `${STORAGE_PREFIX}${panel.dataset.handle}`;
  }

  isDismissed(panel) {
    if (this.isTestMode || this.rememberDays <= 0) return false;

    try {
      const expiry = window.localStorage.getItem(this.storageKey(panel));
      if (!expiry) return false;
      if (Number(expiry) > Date.now()) return true;
      window.localStorage.removeItem(this.storageKey(panel));
    } catch (error) {
      // Private browsing or blocked storage — fall through and show the popup.
    }
    return false;
  }

  rememberDismissal(panel) {
    // Only reachable for a forced panel via the editor's escape hatch — don't let
    // that write a key that would suppress the entry if Force is later turned off.
    if (this.isForced(panel) || this.isTestMode || this.rememberDays <= 0) {
      return;
    }

    try {
      const expiry = Date.now() + this.rememberDays * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(this.storageKey(panel), String(expiry));
    } catch (error) {
      // Storage unavailable — the popup will simply show again next visit.
    }
  }

  open(panel) {
    if (this.activePanel) return;

    this.activePanel = panel;
    this.lastFocusedElement = document.activeElement;

    this.hidden = false;
    panel.hidden = false;
    // Force a frame so the CSS transition runs from its start state.
    window.requestAnimationFrame(() =>
      this.classList.add("country-redirect-popup--visible"),
    );

    // Applies in the theme editor too, so the setting can be verified there. Safe
    // because a forced panel — which has no close button — is still closable with
    // Escape or a backdrop click in design mode, and closing releases the lock.
    if (this.locksScroll) {
      document.documentElement.classList.add("country-redirect-popup--locked");
    }

    document.addEventListener("keydown", this.onKeydown);
    panel
      .querySelectorAll("[data-country-redirect-close]")
      .forEach((button) => {
        button.addEventListener("click", () => this.close());
      });

    // Backdrop click closes, except on a forced panel — where the editor is the
    // exception to the exception, so the merchant can always get out.
    if (!this.isForced(panel) || this.isDesignMode) {
      const backdrop = this.querySelector("[data-country-redirect-backdrop]");
      if (backdrop) backdrop.addEventListener("click", () => this.close());
    }

    const target = panel.querySelector(FOCUSABLE);
    if (target) target.focus();

    if (this.isForced(panel)) this.startForcedRedirect(panel);
  }

  startForcedRedirect(panel) {
    const link = panel.querySelector("[data-country-redirect-link]");
    if (!link) return;

    const countdownEl = panel.querySelector(
      "[data-country-redirect-countdown]",
    );
    let remaining = this.forceCountdown;

    const setText = (text) => {
      if (!countdownEl) return;
      countdownEl.hidden = false;
      countdownEl.textContent = text;
    };

    const render = () => {
      if (!this.forceMessage) return;
      setText(this.forceMessage.replace("[seconds]", remaining));
    };

    const go = () => {
      // Never navigate in the theme editor — it would eject the merchant mid-edit.
      // Say so, rather than leaving the countdown stuck at zero.
      if (this.isDesignMode) {
        setText("Preview only — no redirect happens in the theme editor.");
        return;
      }

      if (this.redirectSuppressed) return;
      window.location.href = link.href;
    };

    if (remaining <= 0) {
      go();
      return;
    }

    render();
    this.countdownTimer = window.setInterval(() => {
      remaining -= 1;
      render();

      if (remaining <= 0) {
        window.clearInterval(this.countdownTimer);
        go();
      }
    }, 1000);
  }

  close() {
    // Forced entries have no close affordance, but guard the method itself so
    // nothing else can dismiss them either — except in the editor.
    if (!this.activePanel) return;
    if (this.isForced(this.activePanel) && !this.isDesignMode) return;

    window.clearInterval(this.countdownTimer);

    const panel = this.activePanel;
    this.rememberDismissal(panel);

    this.classList.remove("country-redirect-popup--visible");
    document.documentElement.classList.remove("country-redirect-popup--locked");
    document.removeEventListener("keydown", this.onKeydown);

    this.activePanel = null;

    window.setTimeout(() => {
      panel.hidden = true;
      this.hidden = true;
    }, 300);

    if (
      this.lastFocusedElement &&
      typeof this.lastFocusedElement.focus === "function"
    ) {
      this.lastFocusedElement.focus();
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
      return;
    }

    if (event.key !== "Tab" || !this.activePanel) return;

    const focusable = Array.from(
      this.activePanel.querySelectorAll(FOCUSABLE),
    ).filter((element) => element.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define("country-redirect-popup", CountryRedirectPopup);
